/** @jsxImportSource ../i18n */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ChevronRight, Download, History as HistoryIcon, RefreshCw, SearchX } from 'lucide-react';
import { fetchClosedPositions } from '../store/slices/portfolioSlice';
import { exportClosedPositions } from '../services/csvExport';
import { BrokerGate } from '../components/shell/Broker';
import { cx } from '../theme/cx';
import { formatDate, formatNumber, formatPercent } from '../theme/format';
import {
  Button, Card, EmptyState, Money, PageHeader, Percent, SearchInput, SkeletonTable, Stat, SymbolMark, Table, TBody, Td, Th,
  THead, Tr,
} from '../components/ui';

// Columns hidden on narrower screens stay readable in the expanded detail row.
const COLUMNS = [
  { key: 'symbol', label: 'Valeur' },
  { key: 'amount', label: 'Qté', align: 'right' },
  { key: 'open_price', label: 'Entrée', align: 'right', className: 'hidden lg:table-cell' },
  { key: 'closing_price', label: 'Sortie', align: 'right', className: 'hidden lg:table-cell' },
  { key: 'closed_profit_loss', label: 'Résultat €', align: 'right' },
  { key: 'closed_profit_loss_pct', label: 'Résultat %', align: 'right' },
  { key: 'open_date', label: 'Ouverture', align: 'right', className: 'hidden 2xl:table-cell' },
  { key: 'close_date', label: 'Clôture', align: 'right' },
  { key: 'holding_period_days', label: 'Durée', align: 'right', className: 'hidden xl:table-cell' },
];

const cellClass = (key) => COLUMNS.find((col) => col.key === key).className;

const ASSET_TYPES = { Stock: 'Action', Etf: 'ETF', Etc: 'ETC', Etn: 'ETN', Bond: 'Obligation', Fund: 'Fonds', MutualFund: 'Fonds', CfdOnStock: 'CFD sur action' };
const DIRECTIONS = { Buy: 'Achat', Sell: 'Vente' };
const CLOSING_METHODS = { Fifo: 'FIFO (premier entré, premier sorti)', Lifo: 'LIFO (dernier entré, premier sorti)', Specific: 'Lignes désignées', Intraday: 'Intrajournalière' };

const ticker = (symbol) => String(symbol || '').split(':')[0];

const sortValue = (position, field) => {
  const value = position[field];
  if (field === 'open_date' || field === 'close_date') return new Date(value).getTime() || 0;
  return typeof value === 'string' ? value.toLowerCase() : value ?? 0;
};

const Detail = ({ label, children }) => (
  <div>
    <dt className="eyebrow">{label}</dt>
    <dd className="mt-1.5 text-sm text-fg">{children}</dd>
  </div>
);

const ClosedRow = ({ position: p, rowId, open, onToggle }) => {
  const detailId = `history-detail-${rowId}`;
  const currency = p.currency || 'EUR';
  return (
    <>
      <Tr onClick={onToggle} className={cx('cursor-pointer', open && 'bg-surface-2/60')}>
        <Td className="w-full max-w-0 min-w-[12rem]">
          <div className="flex items-center gap-3">
            <button
              type="button"
              aria-expanded={open}
              aria-controls={detailId}
              aria-label={`${open ? 'Masquer' : 'Afficher'} le détail de ${ticker(p.symbol)}`}
              onClick={(event) => { event.stopPropagation(); onToggle(); }}
              className="inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md text-fg-subtle transition-colors hover:bg-surface-3 hover:text-fg"
            >
              <ChevronRight size={15} aria-hidden="true" className={cx('transition-transform duration-200', open && 'rotate-90 text-gold-400')} />
            </button>
            <SymbolMark symbol={p.symbol} size="sm" />
            <div className="min-w-0">
              <Link to={`/stock/${encodeURIComponent(p.symbol)}`} onClick={(event) => event.stopPropagation()} className="block truncate font-medium text-fg transition-colors hover:text-gold-200">
                {ticker(p.symbol)}
              </Link>
              <p className="truncate text-xs text-fg-subtle">{p.description}</p>
            </div>
          </div>
        </Td>
        <Td align="right" numeric>{formatNumber(p.amount, { decimals: Number.isInteger(Number(p.amount)) ? 0 : 2 })}</Td>
        <Td align="right" className={cx('whitespace-nowrap', cellClass('open_price'))}><Money value={p.open_price} sensitive={false} currency={currency} /></Td>
        <Td align="right" className={cx('whitespace-nowrap', cellClass('closing_price'))}><Money value={p.closing_price} sensitive={false} currency={currency} /></Td>
        <Td align="right" className="whitespace-nowrap"><Money value={p.closed_profit_loss} sign colorize className="font-medium" /></Td>
        <Td align="right" className="whitespace-nowrap"><Percent value={p.closed_profit_loss_pct} decimals={2} colorize className="font-medium" /></Td>
        <Td align="right" numeric className={cx('whitespace-nowrap', cellClass('open_date'))}><span className="text-fg-muted">{formatDate(p.open_date)}</span></Td>
        <Td align="right" numeric className="whitespace-nowrap"><span className="text-fg-muted">{formatDate(p.close_date)}</span></Td>
        <Td align="right" numeric className={cx('whitespace-nowrap', cellClass('holding_period_days'))}><span className="text-fg-muted">{formatNumber(p.holding_period_days)} j</span></Td>
      </Tr>
      <tr id={detailId} hidden={!open}>
        <td colSpan={COLUMNS.length} className="border-b border-line/70 bg-surface-2/40 px-4 py-5 sm:px-6">
          <dl className="grid grid-cols-2 gap-x-6 gap-y-5 lg:grid-cols-4">
            <Detail label="Type d’actif">{ASSET_TYPES[p.asset_type] || p.asset_type || '—'}</Detail>
            <Detail label="Sens">{DIRECTIONS[p.buy_or_sell] || p.buy_or_sell || '—'}</Detail>
            <Detail label="Devise">{p.currency || '—'}</Detail>
            <Detail label="Méthode de clôture">{CLOSING_METHODS[p.closing_method] || p.closing_method || '—'}</Detail>
            <Detail label="Prix de revient total"><Money value={p.amount * p.open_price} currency={currency} /></Detail>
            <Detail label="Montant de cession"><Money value={p.amount * p.closing_price} currency={currency} /></Detail>
            <Detail label="Date d’ouverture">{formatDate(p.open_date, 'long')}</Detail>
            <Detail label="Date de clôture">{formatDate(p.close_date, 'long')}</Detail>
            <Detail label="Prix d’entrée · de sortie">
              <Money value={p.open_price} sensitive={false} currency={currency} /> · <Money value={p.closing_price} sensitive={false} currency={currency} />
            </Detail>
            <Detail label="Durée de détention">{formatNumber(p.holding_period_days)} jours</Detail>
          </dl>
        </td>
      </tr>
    </>
  );
};

const History = () => {
  const dispatch = useDispatch();
  const { closedPositions, closedPositionsLoading, saxoConnected } = useSelector((state) => state.portfolio);
  const [sort, setSort] = useState({ key: 'close_date', direction: 'desc' });
  const [searchTerm, setSearchTerm] = useState('');
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    if (saxoConnected) {
      dispatch(fetchClosedPositions());
    }
  }, [dispatch, saxoConnected]);

  const positions = useMemo(() => closedPositions?.positions || [], [closedPositions]);
  const stats = closedPositions || {};
  const winRate = stats.count > 0 ? ((stats.winners || 0) / stats.count) * 100 : null;

  const sorted = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const filtered = positions.filter((p) => !term || (p.symbol || '').toLowerCase().includes(term) || (p.description || '').toLowerCase().includes(term));
    const factor = sort.direction === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const aVal = sortValue(a, sort.key);
      const bVal = sortValue(b, sort.key);
      if (aVal < bVal) return -factor;
      if (aVal > bVal) return factor;
      return 0;
    });
  }, [positions, searchTerm, sort]);

  const onSort = (key) => setSort((current) => (current.key === key
    ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
    : { key, direction: key === 'symbol' ? 'asc' : 'desc' }));

  const showStats = positions.length > 0 || !closedPositionsLoading;

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow="Patrimoine"
        title="Historique"
        description="Positions clôturées et résultats réalisés."
        actions={saxoConnected && (
          <>
            <Button variant="secondary" leadingIcon={Download} disabled={positions.length === 0} onClick={() => exportClosedPositions(sorted)}>
              Exporter CSV
            </Button>
            <Button variant="secondary" leadingIcon={RefreshCw} loading={closedPositionsLoading} onClick={() => dispatch(fetchClosedPositions())}>
              Actualiser
            </Button>
          </>
        )}
      />
      <BrokerGate>
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-5">
            <Stat size="compact" className="col-span-2 lg:col-span-1" label="Résultat réalisé" loading={!showStats} value={<Money value={stats.total_pl || 0} sign colorize />} />
            <Stat size="compact" label="Opérations" loading={!showStats} value={<span className="num">{formatNumber(stats.count || 0)}</span>} />
            <Stat size="compact" label="Taux de réussite" loading={!showStats} value={<span className="num">{formatPercent(winRate, { sign: false })}</span>} />
            <Stat size="compact" label="Gagnantes" loading={!showStats} value={<span className="num text-gain-400">{formatNumber(stats.winners || 0)}</span>} />
            <Stat size="compact" label="Perdantes" loading={!showStats} value={<span className="num text-loss-400">{formatNumber(stats.losers || 0)}</span>} />
          </div>

          <Card>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <SearchInput
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClear={() => setSearchTerm('')}
                placeholder="Rechercher par valeur ou libellé…"
                aria-label="Rechercher une opération"
                className="w-full sm:max-w-sm"
              />
              <p className="num whitespace-nowrap text-sm text-fg-muted">
                {sorted.length} {sorted.length > 1 ? 'opérations' : 'opération'}
              </p>
            </div>

            <div className="mt-5">
              {closedPositionsLoading && positions.length === 0 ? (
                <div role="status">
                  <span className="sr-only">Chargement de l’historique…</span>
                  <SkeletonTable rows={6} cols={6} />
                </div>
              ) : positions.length === 0 ? (
                <EmptyState icon={HistoryIcon} title="Aucune position clôturée" description="Vos opérations soldées apparaîtront ici." />
              ) : sorted.length === 0 ? (
                <EmptyState icon={SearchX} title="Aucune opération ne correspond à la recherche" />
              ) : (
                <Table>
                  <THead>
                    <tr>
                      {COLUMNS.map((col) => (
                        <Th
                          key={col.key}
                          align={col.align}
                          className={col.className}
                          sortable
                          sortDirection={sort.key === col.key ? sort.direction : undefined}
                          onSort={() => onSort(col.key)}
                        >
                          {col.label}
                        </Th>
                      ))}
                    </tr>
                  </THead>
                  <TBody>
                    {sorted.map((p, index) => {
                      const rowId = p.closed_position_unique_id || `${p.symbol}-${index}`;
                      return (
                        <ClosedRow
                          key={rowId}
                          position={p}
                          rowId={String(rowId).replace(/[^\w-]/g, '_')}
                          open={expanded === rowId}
                          onToggle={() => setExpanded((current) => (current === rowId ? null : rowId))}
                        />
                      );
                    })}
                  </TBody>
                </Table>
              )}
            </div>
          </Card>
        </div>
      </BrokerGate>
    </div>
  );
};

export default History;
